import type React from "react";
import { useRef } from "react";

export interface DragOffset {
  x: number;
  y: number;
}

/** Keeps the bar's top-centre anchor on screen so it can never be dragged out of reach. */
export const clampDragOffset = (
  offset: DragOffset,
  viewport: { width: number; height: number }
): DragOffset => ({
  x: Math.min(Math.max(offset.x, -viewport.width / 2), viewport.width / 2),
  y: Math.min(Math.max(offset.y, 0), Math.max(viewport.height - 48, 0))
});

/**
 * Drags the element by writing `--drag-x` / `--drag-y` straight onto it, so a
 * pointer move neither re-renders nor mints a new emotion class.
 */
export const useDragOffset = <T extends HTMLElement>() => {
  const targetRef = useRef<T>(null);
  const offset = useRef<DragOffset>({ x: 0, y: 0 });

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) {
      return;
    }
    const handle = event.currentTarget;
    const start = {
      x: event.clientX - offset.current.x,
      y: event.clientY - offset.current.y
    };
    handle.setPointerCapture(event.pointerId);

    const move = (moveEvent: PointerEvent) => {
      offset.current = clampDragOffset(
        { x: moveEvent.clientX - start.x, y: moveEvent.clientY - start.y },
        { width: window.innerWidth, height: window.innerHeight }
      );
      targetRef.current?.style.setProperty("--drag-x", `${offset.current.x}px`);
      targetRef.current?.style.setProperty("--drag-y", `${offset.current.y}px`);
    };
    const stop = () => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", stop);
      handle.removeEventListener("pointercancel", stop);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", stop);
    handle.addEventListener("pointercancel", stop);
  };

  return { targetRef, handleProps: { onPointerDown } };
};
